import React from "react";
import {Chart} from "primereact/chart";
import Icon from "../../../system/components/icon";
import {formatPersianDate} from "../../../models/goal-update/goal-update-api";
import {PROJECT_STATES} from "../../../models/project-update/project-update-api";

// «نکات برجسته» widgets (docs/tasks/18-project-updates.md, design zofino-project-update-with-highlights.html).
// Every widget draws from one figures object: live figures on the update page, the published snapshot in the modal.

const fa = (value) => Number(value || 0).toLocaleString("fa-IR");
const TEAL = "#0cc0cc";
const ORANGE = "#e6531e";
const AMBER = "#f59e0b";
const STONE = "#a8a29e";

export const WIDGET_LIBRARY = [
    {type: "progress", title: "پیشرفت کلی پروژه", description: "درصد پیشرفت واقعی پروژه در مقایسه با برنامه", icon: "donut_large", category: "progress"},
    {type: "taskStatus", title: "وضعیت کارهای پروژه", description: "توزیع کارها بر اساس وضعیت فعلی", icon: "task_alt", category: "tasks"},
    {type: "milestones", title: "پیشرفت مایلستون‌ها", description: "درصد تکمیل خروجی‌های اصلی پروژه", icon: "flag", category: "progress"},
    {type: "burnup", title: "روند برنامه و انجام کار", description: "مقایسه تجمعی کار تعریف‌شده و تکمیل‌شده", icon: "show_chart", category: "time"},
    {type: "overdue", title: "کارهای عقب‌افتاده", description: "کارهایی که موعدشان گذشته و هنوز تکمیل نشده‌اند", icon: "event_busy", category: "risk"},
    {type: "workload", title: "بار کاری اعضای پروژه", description: "کارهای باز هر عضو در مقایسه با بقیه", icon: "groups", category: "team"},
    {type: "completionTrend", title: "روند تکمیل کارها", description: "تعداد کارهای تکمیل‌شده در شش هفته اخیر", icon: "trending_up", category: "tasks"},
    {type: "risks", title: "ریسک‌های باز پروژه", description: "ریسک‌ها و تصمیم‌های ثبت‌شده در بروزرسانی‌های اخیر", icon: "warning", category: "risk"},
    {type: "timeHealth", title: "سلامت زمان‌بندی", description: "مقایسه زمان مصرف‌شده با پیشرفت واقعی", icon: "schedule", category: "time"},
];
export const WIDGET_CATEGORIES = [
    {value: "all", label: "همه"},
    {value: "progress", label: "پیشرفت"},
    {value: "tasks", label: "کارها"},
    {value: "time", label: "زمان"},
    {value: "team", label: "تیم"},
    {value: "risk", label: "ریسک"},
];
export const widgetDefinition = (type) => WIDGET_LIBRARY.find((widget) => widget.type === type);

const chartOptions = (extra = {}) => ({
    maintainAspectRatio: false,
    animation: false,
    plugins: {legend: {position: "bottom", labels: {font: {family: "inherit", size: 11}, boxWidth: 10}}, tooltip: {rtl: true}},
    ...extra,
});

function Bar({value, color = TEAL, height = ".4rem"}) {
    return (
        <div className="w-full border-round-2xl overflow-hidden" style={{height, background: "#f0eded"}}>
            <div style={{width: `${Math.max(0, Math.min(100, Number(value) || 0))}%`, height: "100%", background: color, borderRadius: 999}}/>
        </div>
    );
}

function Empty({children}) {
    return <p className="m-0 text-xs text-500 py-3">{children}</p>;
}

function ProgressWidget({metrics}) {
    const progress = metrics.progress || 0;
    const planned = metrics.planned;
    const circumference = 2 * Math.PI * 48;
    const gap = metrics.gap;
    return (
        <div className="flex align-items-center gap-4">
            <div className="relative flex-none" style={{width: "7rem", height: "7rem"}}>
                <svg viewBox="0 0 120 120" style={{width: "100%", height: "100%", transform: "rotate(-90deg)"}}>
                    <circle cx="60" cy="60" r="48" fill="none" stroke="#f0eded" strokeWidth="10"/>
                    <circle cx="60" cy="60" r="48" fill="none" stroke={TEAL} strokeWidth="10" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference * (1 - progress / 100)}/>
                </svg>
                <div className="absolute flex flex-column align-items-center justify-content-center" style={{inset: 0}}>
                    <strong className="text-2xl">{fa(progress)}٪</strong>
                    <span className="text-2xs text-500">پیشرفت</span>
                </div>
            </div>
            <div className="flex-1 flex flex-column gap-2">
                <div className="flex justify-content-between text-xs"><span className="text-600">برنامه تا امروز</span><strong>{planned === null || planned === undefined ? "—" : `${fa(planned)}٪`}</strong></div>
                <Bar value={planned} color={STONE}/>
                <div className="flex justify-content-between text-xs"><span className="text-600">پیشرفت واقعی</span><strong style={{color: TEAL}}>{fa(progress)}٪</strong></div>
                <Bar value={progress}/>
                {gap === null || gap === undefined
                    ? <p className="m-0 text-2xs text-500">برای مقایسه با برنامه، تاریخ شروع و پایان پروژه را تعیین کنید.</p>
                    : gap > 0
                        ? <p className="m-0 text-2xs px-2 py-1 border-round-lg" style={{background: "#fffbeb", color: "#b45309"}}>{fa(gap)}٪ عقب‌تر از برنامه.</p>
                        : <p className="m-0 text-2xs px-2 py-1 border-round-lg" style={{background: "#ecfeff", color: "#0e7490"}}>مطابق یا جلوتر از برنامه.</p>}
            </div>
        </div>
    );
}

function TaskStatusWidget({metrics}) {
    const tasks = metrics.tasks || {};
    if (!tasks.total) return <Empty>این پروژه هنوز کاری ندارد.</Empty>;
    const data = {
        labels: ["برای انجام", "در حال انجام", "مسدود", "انجام شده"],
        datasets: [{data: [tasks.todo, tasks.doing, tasks.blocked, tasks.done], backgroundColor: [STONE, AMBER, ORANGE, TEAL], borderWidth: 0}],
    };
    return (
        <div className="flex align-items-center gap-3">
            <div style={{width: "9rem", height: "9rem"}} className="flex-none">
                <Chart type="doughnut" data={data} options={chartOptions({cutout: "68%", plugins: {legend: {display: false}}})} style={{height: "100%"}}/>
            </div>
            <div className="flex-1 grid m-0">
                {data.labels.map((label, index) => (
                    <div key={label} className="col-6 p-1 flex align-items-center gap-2 text-xs">
                        <span className="border-circle flex-none" style={{width: ".55rem", height: ".55rem", background: data.datasets[0].backgroundColor[index]}}/>
                        <span className="text-600">{label}</span>
                        <strong className="mr-auto">{fa(data.datasets[0].data[index])}</strong>
                    </div>
                ))}
                <div className="col-12 p-1 text-2xs text-500">مجموع {fa(tasks.total)} کار · {fa(tasks.overdue)} عقب‌افتاده</div>
            </div>
        </div>
    );
}

function MilestonesWidget({metrics}) {
    const milestones = metrics.milestones || [];
    if (!milestones.length) return <Empty>مایلستونی برای این پروژه تعریف نشده است.</Empty>;
    return (
        <div className="flex flex-column gap-3">
            {milestones.slice(0, 8).map((milestone) => (
                <div key={milestone.documentId || milestone.id} className="flex flex-column gap-1">
                    <div className="flex justify-content-between text-xs">
                        <span className="font-bold">{milestone.title}</span>
                        <span style={{color: milestone.percent >= 100 ? TEAL : ORANGE}}>{fa(milestone.percent)}٪</span>
                    </div>
                    <Bar value={milestone.percent} color={milestone.percent >= 100 ? TEAL : ORANGE}/>
                </div>
            ))}
        </div>
    );
}

const weekLabel = (value) => formatPersianDate(value).split(" ").slice(0, 2).join(" ");

function BurnupWidget({metrics}) {
    const rows = metrics.burnup || [];
    if (!rows.some((row) => row.scope)) return <Empty>داده‌ای برای روند وجود ندارد.</Empty>;
    const data = {
        labels: rows.map((row) => weekLabel(row.weekEnd)),
        datasets: [
            {label: "کار تعریف‌شده", data: rows.map((row) => row.scope), borderColor: STONE, backgroundColor: STONE, tension: .3},
            {label: "کار انجام‌شده", data: rows.map((row) => row.done), borderColor: TEAL, backgroundColor: TEAL, tension: .3},
        ],
    };
    return <div style={{height: "12rem"}}><Chart type="line" data={data} options={chartOptions({scales: {y: {beginAtZero: true, ticks: {precision: 0}}}})} style={{height: "100%"}}/></div>;
}

function OverdueWidget({metrics}) {
    const overdue = metrics.overdue || {items: []};
    return (
        <div className="flex flex-column gap-3">
            <div className="grid m-0">
                {[
                    {label: "کل عقب‌افتاده", value: fa(overdue.count), color: ORANGE, background: "rgba(230,83,30,.06)"},
                    {label: "بیش از یک هفته", value: fa(overdue.critical), color: "#b45309", background: "#fffbeb"},
                    {label: "میانگین تأخیر", value: `${fa(overdue.averageDays)} روز`, color: "#292524", background: "#fafaf9"},
                ].map((tile) => (
                    <div key={tile.label} className="col-4 p-1">
                        <div className="p-2 border-round-xl" style={{background: tile.background}}>
                            <span className="text-2xs text-500">{tile.label}</span>
                            <strong className="block text-lg mt-1" style={{color: tile.color}}>{tile.value}</strong>
                        </div>
                    </div>
                ))}
            </div>
            {overdue.items?.length ? overdue.items.map((item, index) => (
                <div key={item.documentId || index} className="flex align-items-center gap-2 p-2 border-round-xl" style={{background: "#fafaf9"}}>
                    <span className="border-circle flex-none" style={{width: ".5rem", height: ".5rem", background: index === 0 ? ORANGE : AMBER}}/>
                    <div className="flex-1 min-w-0">
                        <p className="m-0 text-xs font-bold white-space-nowrap overflow-hidden text-overflow-ellipsis">{item.title}</p>
                        {item.assignee && <p className="m-0 text-2xs text-500">{item.assignee}</p>}
                    </div>
                    <span className="text-2xs font-bold" style={{color: index === 0 ? ORANGE : "#b45309"}}>{fa(item.daysLate)} روز</span>
                </div>
            )) : <Empty>کار عقب‌افتاده‌ای وجود ندارد.</Empty>}
        </div>
    );
}

function WorkloadWidget({metrics}) {
    const rows = metrics.workload || [];
    if (!rows.length) return <Empty>هنوز کاری به اعضا واگذار نشده است.</Empty>;
    return (
        <div className="flex flex-column gap-3">
            {rows.map((row) => {
                const heavy = rows.length > 1 && row.load >= 80;
                return (
                    <div key={row.name} className="flex flex-column gap-1">
                        <div className="flex justify-content-between align-items-center text-xs">
                            <span><strong>{row.name}</strong><span className="text-2xs text-500 mr-2">{fa(row.open)} کار باز{row.overdue ? ` · ${fa(row.overdue)} عقب‌افتاده` : ""}</span></span>
                            <span className="text-2xs" style={{color: heavy ? ORANGE : TEAL}}>{heavy ? "بیشترین بار" : "متعادل"}</span>
                        </div>
                        <Bar value={row.load} color={heavy ? ORANGE : TEAL}/>
                    </div>
                );
            })}
        </div>
    );
}

function CompletionTrendWidget({metrics}) {
    const rows = metrics.completionTrend || [];
    const data = {
        labels: rows.map((row) => weekLabel(row.weekEnd)),
        datasets: [{label: "کار تکمیل‌شده", data: rows.map((row) => row.done), backgroundColor: TEAL, borderRadius: 6}],
    };
    return <div style={{height: "11rem"}}><Chart type="bar" data={data} options={chartOptions({plugins: {legend: {display: false}}, scales: {y: {beginAtZero: true, ticks: {precision: 0}}}})} style={{height: "100%"}}/></div>;
}

function RisksWidget({metrics}) {
    const risks = metrics.risks || [];
    return (
        <div className="flex flex-column gap-2">
            <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 border-round-lg" style={{background: "rgba(230,83,30,.06)", color: ORANGE}}>{fa(metrics.tasks?.blocked)} کار مسدود</span>
                <span className="px-2 py-1 border-round-lg" style={{background: "#fffbeb", color: "#b45309"}}>{fa(metrics.overdue?.critical)} کار با تأخیر جدی</span>
            </div>
            {risks.length ? risks.map((risk, index) => (
                <div key={index} className="p-2 border-round-xl" style={{background: "#fafaf9", borderRight: `3px solid ${risk.state === "out-of-way" ? ORANGE : AMBER}`}}>
                    <p className="m-0 text-xs font-bold">{risk.title}</p>
                    {risk.action && <p className="m-0 mt-1 text-2xs text-600">اقدام: {risk.action}</p>}
                    <p className="m-0 mt-1 text-2xs text-500">{PROJECT_STATES[risk.state]?.label} · {formatPersianDate(risk.at)}{risk.reviewAt ? ` · بررسی: ${formatPersianDate(risk.reviewAt)}` : ""}</p>
                </div>
            )) : <Empty>در بروزرسانی‌های اخیر ریسکی ثبت نشده است.</Empty>}
        </div>
    );
}

function TimeHealthWidget({metrics}) {
    const elapsed = metrics.elapsed;
    const progress = metrics.progress || 0;
    return (
        <div className="flex flex-column gap-3">
            <div className="grid m-0">
                <div className="col-6 p-1">
                    <div className="p-3 border-round-2xl" style={{background: "#fafaf9"}}>
                        <span className="text-2xs text-500">زمان سپری‌شده</span>
                        <strong className="block text-2xl mt-1">{elapsed === null || elapsed === undefined ? "—" : `${fa(elapsed)}٪`}</strong>
                        <div className="mt-2"><Bar value={elapsed} color="#78716c"/></div>
                    </div>
                </div>
                <div className="col-6 p-1">
                    <div className="p-3 border-round-2xl" style={{background: "#ecfeff"}}>
                        <span className="text-2xs text-500">پیشرفت واقعی</span>
                        <strong className="block text-2xl mt-1" style={{color: TEAL}}>{fa(progress)}٪</strong>
                        <div className="mt-2"><Bar value={progress}/></div>
                    </div>
                </div>
            </div>
            <p className="m-0 text-xs font-bold" style={{color: elapsed !== null && elapsed !== undefined && elapsed > progress ? "#b45309" : "#0e7490"}}>
                {elapsed === null || elapsed === undefined
                    ? "تاریخ شروع و پایان پروژه مشخص نیست."
                    : elapsed > progress
                        ? `پیشرفت ${fa(elapsed - progress)}٪ از زمان مصرف‌شده عقب است.`
                        : `پروژه ${fa(progress - elapsed)}٪ جلوتر از زمان مصرف‌شده پیش می‌رود.`}
                {metrics.daysLeft !== null && metrics.daysLeft !== undefined && ` ${metrics.daysLeft >= 0 ? `${fa(metrics.daysLeft)} روز تا پایان.` : "موعد پایان گذشته است."}`}
            </p>
        </div>
    );
}

const RENDERERS = {
    progress: ProgressWidget,
    taskStatus: TaskStatusWidget,
    milestones: MilestonesWidget,
    burnup: BurnupWidget,
    overdue: OverdueWidget,
    workload: WorkloadWidget,
    completionTrend: CompletionTrendWidget,
    risks: RisksWidget,
    timeHealth: TimeHealthWidget,
};

// one widget card; `actions` holds remove / drag controls on the update page
export function WidgetCard({type, metrics, actions, dragProps = {}, compact = false}) {
    const definition = widgetDefinition(type);
    const Renderer = RENDERERS[type];
    if (!definition || !Renderer || !metrics) return null;
    return (
        <article className={`pu-widget ${compact ? "pu-widget--compact" : ""}`} {...dragProps}>
            <header className="flex align-items-start justify-content-between gap-2 mb-3">
                <div className="flex align-items-center gap-2">
                    <span className="pu-widget__icon"><Icon icon={`mi mi:${definition.icon}`} className="text-lg"/></span>
                    <div>
                        <h4 className="m-0 text-sm font-bold">{definition.title}</h4>
                        {!compact && <p className="m-0 text-2xs text-500">{definition.description}</p>}
                    </div>
                </div>
                {actions}
            </header>
            <Renderer metrics={metrics}/>
        </article>
    );
}
